import React, { useRef, useState, useEffect, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { useTranslate } from "@tolgee/react";
import { useContextMenu } from "react-contexify";
import { Popover } from "react-tiny-popover";
import InputContextMenu from "../Chat/InputContextMenu";
import CallChatAttachmentMenu from "./CallChatAttachmentMenu";
import CallChatFileAttachmentItem from "./CallChatFileAttachmentItem";
import { ReactComponent as SendIcon } from "../../Assets/iconsv2/chat/SendButton.svg";
import { ReactComponent as SendAttachments } from "../../Assets/iconsv2/chat/MediaButton.svg";
import { ReactComponent as SendAttachmentsLight } from "../../Assets/iconsv2/chat/MediaButtonLight.svg";
import { ReactComponent as MediaIcon } from "../../Assets/icons/context-menu-icons/MediaIcon.svg";
import { ReactComponent as FileIcon } from "../../Assets/icons/context-menu-icons/FilesIcon.svg";
import { ReactComponent as CloseIcon } from "../../Assets/iconsv2/generic/closeBtn.svg";
import { useGenericComponentsStateService } from "../../Context/GenericComponentsStateContext";
import { useNotify } from "../../Helpers/Notifications";

const CallChatInput = ({ 
  onSendMessage, 
  disabled, 
  placeholder,
  mediaInputFileRef,
  fileInputRef,
  setMediaInput,
  setFileInput,
  messageInputRef,
  initialValue = "",
  mediaInput,
  fileInput,
  showRecipientSelector = false,
  recipientOptions = [],
  selectedRecipient = null,
  onSelectRecipient,
}) => {
  const { t } = useTranslate();
  const { theme } = useGenericComponentsStateService();
  const { notifySuccess } = useNotify();

  const [messageText, setMessageText] = useState("");
  const [isAttachmentMenuOpen, setIsAttachmentMenuOpen] = useState(false);
  const [isRecipientMenuOpen, setIsRecipientMenuOpen] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null);
  const textareaRef = useRef(null);

  const MENU_ID = "call-chat-input-context-menu";
  const { show } = useContextMenu({ id: MENU_ID });

  useEffect(() => {
    setMessageText(initialValue);
  }, [initialValue]);

  useEffect(() => {
    if (messageInputRef) {
      messageInputRef.current = textareaRef.current;
    }
  }, [messageInputRef]);

  useEffect(() => {
    if (!mediaInput) {
      setMediaPreview(null);
      return;
    }

    const previewUrl = URL.createObjectURL(mediaInput);
    setMediaPreview(previewUrl);

    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [mediaInput]);

  const handleMediaInputFileChange = (e) => {
    if (!e.target.files) return;

    const inputFile = e.target.files[0];
    const fileType = inputFile.type.split("/")[0];
    
    if (fileType === "image" || fileType === "video") {
      setMediaInput(inputFile);
      setIsAttachmentMenuOpen(false);
    }
  };

  const handleInputFileChange = (e) => {
    if (!e.target.files) return;
    
    const inputFile = e.target.files[0];
    setFileInput(inputFile);
    setIsAttachmentMenuOpen(false);
  };

  const handleRemoveMedia = () => {
    if (mediaInputFileRef?.current) {
      mediaInputFileRef.current.value = null;
    }
    setMediaInput(null);
    setMediaPreview(null);
  };

  const handleRemoveFile = () => {
    if (fileInputRef?.current) {
      fileInputRef.current.value = null;
    }
    setFileInput(null);
  };

  useEffect(() => {
    const handlePaste = (event) => {
      if (!event.clipboardData) return;

      const files = event.clipboardData.files;
      if (files.length > 0) {
        event.preventDefault();

        const pastedFile = files[0];
        const fileType = pastedFile.type.split("/")[0];

        if (fileType === "image" || fileType === "video") {
          setMediaInput(pastedFile);
          notifySuccess(t('chat.media_file_pasted'));
        } else {
          setFileInput(pastedFile);
          notifySuccess(t('chat.file_pasted'));
        }
      }
    };

    if (textareaRef.current) {
      textareaRef.current.addEventListener("paste", handlePaste);
    }

    return () => {
      if (textareaRef.current) {
        textareaRef.current.removeEventListener("paste", handlePaste);
      }
    };
  }, [setMediaInput, setFileInput, notifySuccess, t]);

  const attachmentOptionsList = useMemo(() => {
    return [
      {
        text: t("general.media"),
        icon: <MediaIcon />,
        action: () => {
          if (mediaInputFileRef?.current) {
            mediaInputFileRef.current.click();
            setIsAttachmentMenuOpen(false);
          }
        },
      },
      {
        text: t("general.files"),
        icon: <FileIcon />,
        action: () => {
          if (fileInputRef?.current) {
            fileInputRef.current.click();
            setIsAttachmentMenuOpen(false);
          }
        },
      },
    ];
  }, [t, mediaInputFileRef, fileInputRef]);

  const handleSend = () => {
    if ((!messageText.trim() && !mediaInput && !fileInput) || disabled) return;
    
    onSendMessage(messageText);
    setMessageText("");
    
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChange = (e) => {
    setMessageText(e.target.value);
    
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    
    if (textareaRef.current) {
      const customEvent = new CustomEvent("update-selection", {
        detail: {
          selection: {
            start: textareaRef.current.selectionStart,
            end: textareaRef.current.selectionEnd,
          },
        },
      });
      textareaRef.current.dispatchEvent(customEvent);
    }
    
    show({ event: e });
  };

  return (
    <Container>
      <InputContextMenu
        inputRef={textareaRef}
        onCopy={() => {}}
        onCut={(e) => setMessageText(e.target.value)}
        onPaste={(e) => setMessageText(e.target.value)}
      />
      
      <input
        type="file"
        hidden
        ref={mediaInputFileRef}
        onChange={handleMediaInputFileChange}
        accept="image/*, video/*"
      />
      <input
        type="file"
        hidden
        ref={fileInputRef}
        onChange={handleInputFileChange}
        accept="application/*"
      />
      
      {showRecipientSelector && (
        <RecipientRow>
          <RecipientLabel>{t("conferences.chat_send_to")}</RecipientLabel>
          <Popover
            isOpen={isRecipientMenuOpen}
            onClickOutside={() => setIsRecipientMenuOpen(false)}
            positions={["top"]}
            padding={6}
            align="start"
            containerStyle={{ zIndex: 10000 }}
            content={
              <RecipientMenu onClick={(e) => e.stopPropagation()}>
                <RecipientOption
                  $active={!selectedRecipient}
                  onClick={() => {
                    onSelectRecipient && onSelectRecipient(null);
                    setIsRecipientMenuOpen(false);
                  }}
                >
                  {t("conferences.chat_audience_everyone")}
                </RecipientOption>
                {recipientOptions.map((option) => (
                  <RecipientOption
                    key={option.id}
                    $active={selectedRecipient?.id === option.id}
                    onClick={() => {
                      onSelectRecipient && onSelectRecipient(option);
                      setIsRecipientMenuOpen(false);
                    }}
                  >
                    {option.name}
                  </RecipientOption>
                ))}
              </RecipientMenu>
            }
          >
            <RecipientPill
              $private={!!selectedRecipient}
              onClick={(e) => {
                e.stopPropagation();
                setIsRecipientMenuOpen((prev) => !prev);
              }}
            >
              {selectedRecipient
                ? selectedRecipient.name
                : t("conferences.chat_audience_everyone")}
              <span>▾</span>
            </RecipientPill>
          </Popover>
        </RecipientRow>
      )}

      <InputWrapper hasAttachment={!!(mediaInput || fileInput)}>
        {(mediaInput || fileInput) && (
          <AttachmentPreviewArea>
            {mediaInput && mediaPreview && (
              <MediaPreviewItem>
                <MediaPreviewImage>
                  <img src={mediaPreview} alt="preview" />
                  <RemoveButton onClick={handleRemoveMedia}>
                    <CloseIcon />
                  </RemoveButton>
                </MediaPreviewImage>
                <MediaFileName>{mediaInput.name}</MediaFileName>
              </MediaPreviewItem>
            )}
            
            {fileInput && (
              <FilePreviewItem>
                <CallChatFileAttachmentItem file={fileInput} />
                <RemoveFileButton onClick={handleRemoveFile}>
                  <CloseIcon />
                </RemoveFileButton>
              </FilePreviewItem>
            )}
          </AttachmentPreviewArea>
        )}

        <InputRow>
          <Popover
            isOpen={isAttachmentMenuOpen}
            onClickOutside={() => {
              setIsAttachmentMenuOpen(false);
            }}
            positions={["top"]}
            padding={8}
            align="start"
            containerStyle={{ zIndex: 10000 }}
            content={
              <div 
                style={{ 
                  zIndex: 10001,
                  pointerEvents: 'auto',
                  position: 'relative'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <CallChatAttachmentMenu optionsList={attachmentOptionsList} />
              </div>
            }
          >
            <AttachmentButton
              onClick={(e) => {
                e.stopPropagation();
                setIsAttachmentMenuOpen(prev => !prev);
              }}
              disabled={disabled}
            >
              {theme === "light" ? <SendAttachmentsLight /> : <SendAttachments />}
            </AttachmentButton>
          </Popover>
          
          <StyledTextarea
            ref={textareaRef}
            value={messageText}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
            onContextMenu={handleContextMenu}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
          />
          
          <SendButton 
            onClick={handleSend} 
            disabled={(!messageText.trim() && !mediaInput && !fileInput) || disabled}
          >
            <SendIcon />
          </SendButton>
        </InputRow>
      </InputWrapper>
    </Container>
  );
};

export default CallChatInput;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 16px;
  background: ${({ theme }) => theme.callComponent.participantsListBackground};
  border-top: 1px solid ${({ theme }) => theme.sidebar.borderColor};
`;

const RecipientRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 4px 8px;
`;

const RecipientLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
`;

const RecipientPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  background: ${({ theme, $private }) =>
    $private ? theme.chat.sendBtn : "transparent"};
  color: ${({ theme, $private }) =>
    $private ? "#fff" : theme.menu.primaryTextColor};
  border-radius: 14px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  span {
    font-size: 10px;
    opacity: 0.8;
  }
`;

const RecipientMenu = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 160px;
  max-height: 220px;
  overflow-y: auto;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  border-radius: 10px;
  padding: 4px;
  z-index: 10001;
`;

const RecipientOption = styled.button`
  text-align: left;
  border: none;
  background: ${({ theme, $active }) =>
    $active ? theme.chat.messageInputWrapperBackground : "transparent"};
  color: ${({ theme }) => theme.menu.primaryTextColor};
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: ${({ theme }) => theme.chat.messageInputWrapperBackground};
  }
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.chat.messageInputWrapperBackground};
  border-radius: 20px;
  padding: ${({ hasAttachment }) => hasAttachment ? '8px' : '8px 12px'};
  gap: ${({ hasAttachment }) => hasAttachment ? '8px' : '0'};
`;

const AttachmentPreviewArea = styled.div`
  display: flex;
  gap: 8px;
  padding: 4px 8px;
  flex-wrap: wrap;
`;

const MediaPreviewItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MediaPreviewImage = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const MediaFileName = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  max-width: 80px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FilePreviewItem = styled.div`
  position: relative;
  display: flex;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  border: none;
  cursor: pointer;
  z-index: 10;
  transition: background 0.2s ease;
  
  svg {
    width: 10px;
    height: 10px;
    color: white;
    stroke: white;
  }
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

const RemoveFileButton = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.chat.sendBtn};
  border: none;
  cursor: pointer;
  z-index: 10;
  transition: transform 0.2s ease;
  
  svg {
    width: 10px;
    height: 10px;
    color: white;
    stroke: white;
  }
  
  &:hover {
    transform: scale(1.1);
  }
`;

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  padding: 0 4px;
`;

const AttachmentButton = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: none;
  background: none;
  flex-shrink: 0;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StyledTextarea = styled.textarea`
  flex: 1;
  min-height: 42px;
  padding: 10px 12px;
  background: transparent;
  border: none;
  outline: none;
  resize: none;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  max-height: 74px;
  overflow-y: auto;
  
  &::placeholder {
    color: ${({ theme }) => theme.menu.secondaryTextColor};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }
`;

const SendButton = styled.button`
  width: 42px;
  height: 42px;
  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: none;
  background: none;
  flex-shrink: 0;

  svg {
    fill: ${({ theme }) => theme.chat.sendBtn};
    color: ${({ theme }) => theme.chat.sendBtnBorder};
  }

  &:disabled {
    opacity: 0.5;
    cursor: inherit;
  }
`;